import { useEffect, useRef, type RefObject, type VideoHTMLAttributes } from 'react'
import type Hls from 'hls.js'

type Props = VideoHTMLAttributes<HTMLVideoElement> & { videoRef: RefObject<HTMLVideoElement | null>; offset: number; src: string; playbackSpeed?: number }

export function preloadProgressivePlayer() { void import('hls.js').catch(() => undefined) }

export function ProgressiveRecordingVideo({ videoRef, offset, src, playbackSpeed = 1, onError, onLoadedMetadata, ...props }: Props) {
  const fallbackRef = useRef<() => void>(() => undefined)
  const native = useRef(false)
  const hlsRef = useRef<Hls | null>(null)
  const bufferRef = useRef({ maxBufferLength: 12, maxMaxBufferLength: 20 })
  const normalizedSpeed = Number.isFinite(playbackSpeed) ? Math.max(1, Math.min(20, playbackSpeed)) : 1
  bufferRef.current = { maxBufferLength: Math.min(120, 12 * normalizedSpeed), maxMaxBufferLength: Math.min(120, 20 * normalizedSpeed) }
  useEffect(() => {
    if (hlsRef.current) Object.assign(hlsRef.current.config, bufferRef.current)
  }, [normalizedSpeed])
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let disposed = false, hls: Hls | null = null, playing = false
    const controller = new AbortController()
    native.current = false
    const fallback = () => {
      if (disposed || native.current) return
      native.current = true
      controller.abort()
      hls?.destroy(); hls = null; hlsRef.current = null
      video.src = src
      video.load()
      void video.play().catch(() => undefined)
    }
    fallbackRef.current = fallback
    const started = () => { playing = true }
    video.addEventListener('playing', started)
    const watchdog = window.setTimeout(() => {
      // A deliberate pause during loading can prevent the first playing event.
      // Ready video is healthy and must keep its requested surveillance time.
      if (!playing && !(video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)) fallback()
    }, 15000)
    void (async () => {
      try {
        const { default: Player } = await import('hls.js')
        if (disposed) return
        if (!Player.isSupported()) { fallback(); return }
        const manifest = src.replace(/\/playback\/[^?]+(?:\?.*)?$/, '/progressive/index.m3u8')
        // Never start at the live edge or at the wrong surveillance timestamp.
        // Wait only until the requested point exists, not for the full recording.
        let target = offset
        while (!disposed && !native.current) {
          const response = await fetch(manifest, { credentials: 'include', cache: 'no-store', signal: controller.signal })
          if (!response.ok) throw new Error('unavailable')
          const playlist = await response.text()
          const duration = [...playlist.matchAll(/#EXTINF:([\d.]+)/g)].reduce((sum, match) => sum + Number(match[1]), 0)
          const complete = playlist.includes('#EXT-X-ENDLIST')
          if (duration > offset + .1 || complete && duration > 0) {
            target = Math.min(offset, Math.max(0, duration - .1)); break
          }
          await new Promise(resolve => window.setTimeout(resolve, 250))
        }
        if (disposed || native.current) return
        hls = new Player({ startPosition: target, ...bufferRef.current,
          backBufferLength: 10, lowLatencyMode: false, xhrSetup: xhr => { xhr.withCredentials = true } })
        hlsRef.current = hls
        hls.on(Player.Events.ERROR, (_event, data) => { if (data.fatal) fallback() })
        hls.on(Player.Events.MANIFEST_PARSED, () => { void video.play().catch(() => undefined) })
        hls.loadSource(manifest)
        hls.attachMedia(video)
      } catch { fallback() }
    })()
    return () => {
      disposed = true
      window.clearTimeout(watchdog)
      controller.abort()
      video.removeEventListener('playing', started)
      hls?.destroy()
      hlsRef.current = null
      video.removeAttribute('src')
      video.load()
    }
  }, [src, videoRef])
  return <video {...props} ref={videoRef}
    onLoadedMetadata={event => { if (native.current) onLoadedMetadata?.(event) }}
    onError={event => { if (native.current) onError?.(event); else fallbackRef.current() }}/>
}
